import {
  OrganizationConnectedPlatform,
  OrganizationIntegrationStatus,
} from 'src/enums'
import {
  OrganizationIntegrationListModel,
  OrganizationIntegrationListResponse,
} from 'src/models/organizationIntegration.models'
import {
  ExtendBaseQueryOptions,
  MutationMethod,
  useExtendMutation,
  useExtendQuery,
} from 'src/queries/queries.utils'

import { orgIntegrationsQueryKeys } from '../queries.constants'

interface OrgIntegrationApiResponse {
  organizationConnection: OrganizationIntegrationListResponse
}

interface QueryOrgIntegrationOptions
  extends ExtendBaseQueryOptions<
    OrgIntegrationApiResponse,
    OrganizationIntegrationListModel
  > {
  orgId: string | undefined
  orgIntegrationId: string | undefined
}
export const useQueryOrgIntegration = (options: QueryOrgIntegrationOptions) => {
  const { orgId, orgIntegrationId } = options
  const enabled = options?.enabled ?? true

  return useExtendQuery<
    OrgIntegrationApiResponse,
    OrganizationIntegrationListModel
  >({
    queryKey: orgIntegrationsQueryKeys.detail(orgIntegrationId),
    queryUrl: () => `/organizations/${orgId}/connections/${orgIntegrationId}`,
    enabled: enabled,
    select: (data) =>
      new OrganizationIntegrationListModel(data.organizationConnection),
  })
}

interface OrgIntegrationsApiResponse {
  organizationConnections: OrganizationIntegrationListResponse[]
}

type UseQueryOrgIntegrationsResult = Partial<
  Record<OrganizationConnectedPlatform, OrganizationIntegrationListModel>
>

interface QueryOrgIntegrationsOptions
  extends ExtendBaseQueryOptions<
    OrgIntegrationsApiResponse,
    UseQueryOrgIntegrationsResult
  > {
  orgId: string | undefined
}
export const useQueryOrgIntegrations = (
  options: QueryOrgIntegrationsOptions,
) => {
  const { orgId } = options
  const enabled = options?.enabled ?? true

  return useExtendQuery<
    OrgIntegrationsApiResponse,
    UseQueryOrgIntegrationsResult
  >({
    queryKey: orgIntegrationsQueryKeys.all,
    queryUrl: () => `/organizations/${orgId}/connections`,
    enabled: enabled,
    select: (data) => {
      return data.organizationConnections.reduce<UseQueryOrgIntegrationsResult>(
        (acc, conn) => {
          const platform =
            conn.connectedPlatform as OrganizationConnectedPlatform
          acc[platform] = new OrganizationIntegrationListModel(conn)
          return acc
        },
        {},
      )
    },
  })
}

export const useActiveOrgIntegration = (
  options: QueryOrgIntegrationsOptions,
): OrganizationIntegrationListModel | undefined => {
  const { data: orgIntegrations } = useQueryOrgIntegrations(options)

  return Object.values(orgIntegrations || {}).find(
    (integration) =>
      integration.connectionStatus === OrganizationIntegrationStatus.ACTIVE,
  )
}

interface ReauthConnectionResponse {
  companyToken: string
  linkUrl: string
  accessToken: string
}

interface UseReauthOrgIntegrationRequest {
  body: Record<string, never>
  orgId: string
  connectionId: string
}

export const useReauthOrgIntegration = () => {
  return useExtendMutation<
    ReauthConnectionResponse,
    UseReauthOrgIntegrationRequest
  >(MutationMethod.POST, {
    mutationUrl: ({ orgId, connectionId }) =>
      `/organizations/${orgId}/connections/${connectionId}/reauth`,
  })
}
